import seoScriptProps from 'utilities/src/seoScriptProps';
import { parseImageUrl } from 'utils/imageUtils';

const getJsonLdObject = (jsonLd: string) => {
  try {
    return JSON.parse(jsonLd);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error parsing JSON-LD:', error);
    return null;
  }
};

const isOfType = (item: any, type: string) => ([] as unknown[]).concat(item?.['@type'] ?? []).includes(type);

interface HandleJsonLdDateModifiedArgs {
  jsonLdContent: string;
  isoDateModified?: string | null;
  isoDatePublished?: string | null;
}

const handleJsonLdDateModified = ({
  jsonLdContent,
  isoDateModified,
  isoDatePublished,
}: HandleJsonLdDateModifiedArgs) => {
  const jsonLdObject = getJsonLdObject(jsonLdContent);
  if (!jsonLdObject) return null;

  const jsonLdObjectWithDateModified = {
    ...jsonLdObject,
    // The page renders its own BreadcrumbList (with the blog taxonomy), so RankMath's one is dropped.
    '@graph': jsonLdObject['@graph']
      ?.filter((item: any) => !isOfType(item, 'BreadcrumbList'))
      .map((graphItem: any) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { breadcrumb, ...item } = graphItem;
        if (!['WebPage', 'Article'].includes(item['@type'])) return item;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { dateModified: originalDateModified, datePublished: originalDatePublished, ...rest } = item;
        const datePublished = isoDatePublished;
        if (!isoDateModified) return { ...rest, datePublished };
        return {
          ...rest,
          dateModified: isoDateModified,
          datePublished,
        };
      }),
  };

  return JSON.stringify(jsonLdObjectWithDateModified);
};

interface Args {
  jsonLdRaw: string;
  isoDateModified?: string | null;
  isoDatePublished?: string | null;
}

const handleJsonLd = ({ jsonLdRaw, isoDateModified, isoDatePublished }: Args) => {
  const scriptProps = seoScriptProps({ raw: jsonLdRaw }, (content) =>
    parseImageUrl(handleJsonLdDateModified({ jsonLdContent: content, isoDateModified, isoDatePublished }) ?? ''),
  );
  if (!scriptProps) return null;
  const { class: className, ...rest } = scriptProps;
  return {
    ...rest,
    className,
  };
};

export default handleJsonLd;
